import { useEffect, useRef, useState } from 'react';
import { Puzzle } from '../../shared/types';
import { GameState, CellState, ValidationResult, cloneBoard, validate, computeAutoXs } from '../game';
import { HintHighlight } from '../hints';

const COLOR_LABELS = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M', 'N'];

interface Props {
  puzzle: Puzzle;
  state: GameState;
  onStateChange: (state: GameState, result: ValidationResult) => void;
  autoXs?: boolean;
  readOnly?: boolean;
  /** Cells to spotlight for the active hint (reasoning area, eliminations, forced placement). */
  highlight?: HintHighlight | null;
  /**
   * Cell keys ("r,c") holding a new player's revealed queens. They are drawn differently and take
   * no clicks: a beginner who taps one and cycles it away has lost the help they were given, with
   * nothing on screen to tell them what happened or how to get it back.
   */
  revealed?: Set<string> | null;
}

export function Board({ puzzle, state, onStateChange, autoXs = false, readOnly = false, highlight = null, revealed = null }: Props) {
  const [liveBoard, setLiveBoard] = useState<CellState[][] | null>(null);
  const boardRef = useRef<HTMLDivElement>(null);
  const drag = useRef({
    active: false,
    startBoard: null as CellState[][] | null,
    cells: [] as Array<{ row: number; col: number }>,
    isSingleCell: true,
  });

  // Refs so event handler closures always see the latest values without re-registering
  const stateRef = useRef(state);
  stateRef.current = state;
  const liveBoardRef = useRef(liveBoard);
  liveBoardRef.current = liveBoard;
  const onChangeRef = useRef(onStateChange);
  onChangeRef.current = onStateChange;
  const revealedRef = useRef(revealed);
  revealedRef.current = revealed;

  useEffect(() => {
    if (readOnly) return;
    const el = boardRef.current!;

    function cellFromPoint(x: number, y: number) {
      const target = document.elementFromPoint(x, y);
      if (!target) return null;
      const cell = (target as HTMLElement).closest<HTMLElement>('.cell');
      if (!cell) return null;
      const row = Number(cell.dataset.row);
      const col = Number(cell.dataset.col);
      if (isNaN(row) || isNaN(col)) return null;
      // A revealed queen is not a square you can mark, so a gesture over one is not a gesture on
      // a cell at all — a drag simply passes across it.
      if (revealedRef.current?.has(`${row},${col}`)) return null;
      return { row, col };
    }

    function startDrag(cell: { row: number; col: number }) {
      const board = cloneBoard(stateRef.current.board);
      const prev = board[cell.row][cell.col];
      board[cell.row][cell.col] = prev === null ? 'X' : prev === 'X' ? 'queen' : null;
      drag.current = { active: true, startBoard: cloneBoard(stateRef.current.board), cells: [cell], isSingleCell: true };
      setLiveBoard(board);
    }

    function extendDrag(cell: { row: number; col: number }) {
      const d = drag.current;
      if (!d.active) return;
      if (d.cells.some(c => c.row === cell.row && c.col === cell.col)) return;

      let board: CellState[][];
      if (d.isSingleCell) {
        d.isSingleCell = false;
        board = cloneBoard(d.startBoard!);
        const first = d.cells[0];
        if (board[first.row][first.col] === null) board[first.row][first.col] = 'X';
      } else {
        board = cloneBoard(liveBoardRef.current ?? stateRef.current.board);
      }

      d.cells.push(cell);
      if (board[cell.row][cell.col] === null) board[cell.row][cell.col] = 'X';
      setLiveBoard(board);
    }

    function endDrag() {
      const d = drag.current;
      if (!d.active) return;
      d.active = false;

      const currentState = stateRef.current;
      const elapsedMs = Date.now() - currentState.startTime;
      const startBoard = d.startBoard!;
      const finalBoard = liveBoardRef.current ?? currentState.board;

      const newState: GameState = d.isSingleCell
        ? {
            ...currentState,
            board: finalBoard,
            undoStack: [...currentState.undoStack, startBoard],
            events: [...currentState.events, { type: 'click', row: d.cells[0].row, col: d.cells[0].col, elapsedMs }],
          }
        : {
            ...currentState,
            board: finalBoard,
            undoStack: [...currentState.undoStack, startBoard],
            events: [...currentState.events, { type: 'drag', cells: d.cells, elapsedMs }],
          };

      setLiveBoard(null);
      const result = validate(finalBoard, puzzle.colorRegions);
      onChangeRef.current(newState, result);
    }

    const onMouseDown = (e: MouseEvent) => {
      if (e.button !== 0) return;
      e.preventDefault();
      const cell = cellFromPoint(e.clientX, e.clientY);
      if (cell) startDrag(cell);
    };
    const onMouseMove = (e: MouseEvent) => {
      if (!drag.current.active) return;
      const cell = cellFromPoint(e.clientX, e.clientY);
      if (cell) extendDrag(cell);
    };
    const onMouseUp = () => { if (drag.current.active) endDrag(); };
    const onTouchStart = (e: TouchEvent) => {
      e.preventDefault();
      const cell = cellFromPoint(e.touches[0].clientX, e.touches[0].clientY);
      if (cell) startDrag(cell);
    };
    const onTouchMove = (e: TouchEvent) => {
      e.preventDefault();
      if (!drag.current.active) return;
      const cell = cellFromPoint(e.touches[0].clientX, e.touches[0].clientY);
      if (cell) extendDrag(cell);
    };
    const onTouchEnd = () => { if (drag.current.active) endDrag(); };

    el.addEventListener('mousedown', onMouseDown);
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    el.addEventListener('touchstart', onTouchStart, { passive: false });
    el.addEventListener('touchmove', onTouchMove, { passive: false });
    el.addEventListener('touchend', onTouchEnd);

    return () => {
      el.removeEventListener('mousedown', onMouseDown);
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchmove', onTouchMove);
      el.removeEventListener('touchend', onTouchEnd);
    };
  }, [puzzle, readOnly]);

  const displayBoard = liveBoard ?? state.board;
  const { size, colorRegions } = puzzle;
  const result = validate(displayBoard, colorRegions);
  const autoXCells = autoXs ? computeAutoXs(displayBoard, colorRegions) : null;

  // A cell is in the hint's "reasoning area" if it belongs to a highlighted row, column, or region.
  // A highlighted row/column covers the whole line (a line is the natural unit even where some of its
  // squares are already marked). A highlighted region covers only its still-open squares — the
  // candidates the deduction actually reasons about — so a region's marked cells don't overstate the
  // area. Cause cells are always included: a "no room left" mistake points at a fully-marked region,
  // and those cells live in `cause`, so it still highlights.
  // The area tint + perimeter glow are drawn from this, so adjacent lines/regions merge into one
  // outlined blob (a side glows only when the neighbor across it is outside the area — the same
  // neighbor-differs rule used for region borders below).
  const isOpen = (r: number, c: number) =>
    displayBoard[r][c] === null && !(autoXCells?.has(`${r},${c}`) ?? false);
  const inArea = (r: number, c: number) => {
    if (!highlight || r < 0 || r >= size || c < 0 || c >= size) return false;
    if (highlight.rows.has(r) || highlight.cols.has(c)) return true;
    if (highlight.cause.has(`${r},${c}`)) return true;
    return highlight.regions.has(colorRegions[r][c]) && isOpen(r, c);
  };

  const cells = [];
  for (let r = 0; r < size; r++) {
    for (let c = 0; c < size; c++) {
      const k = `${r},${c}`;
      const colorIdx = COLOR_LABELS.indexOf(colorRegions[r][c]);
      const cellState = displayBoard[r][c];
      const invalid = cellState === 'queen' && result.invalidCells.has(k);
      const isAutoX = cellState === null && (autoXCells?.has(k) ?? false);
      const isCrosshatch = result.crosshatchCells.has(k);
      const isHintCause = highlight?.cause.has(k) ?? false;
      const isHintElim = highlight?.elim.has(k) ?? false;
      const isHintPlace = highlight?.place.has(k) ?? false;
      const isHintArea = inArea(r, c);

      // Perimeter glow: an inset line + directional outer bloom on each side whose neighbor is
      // outside the area. Composed inline because the 16 side combinations aren't practical as
      // static classes. Vars (--hint-glow) are defined in style.css.
      let boxShadow: string | undefined;
      if (isHintArea) {
        const parts: string[] = [];
        if (!inArea(r - 1, c)) parts.push('inset 0 3px 0 0 var(--hint-glow)', '0 -4px 7px -2px var(--hint-glow)');
        if (!inArea(r + 1, c)) parts.push('inset 0 -3px 0 0 var(--hint-glow)', '0 4px 7px -2px var(--hint-glow)');
        if (!inArea(r, c - 1)) parts.push('inset 3px 0 0 0 var(--hint-glow)', '-4px 0 7px -2px var(--hint-glow)');
        if (!inArea(r, c + 1)) parts.push('inset -3px 0 0 0 var(--hint-glow)', '4px 0 7px -2px var(--hint-glow)');
        if (parts.length) boxShadow = parts.join(', ');
      }

      const cls = [
        'cell',
        `color-${colorIdx}`,
        r === 0 || colorRegions[r - 1][c] !== colorRegions[r][c] ? 'border-top' : '',
        c === 0 || colorRegions[r][c - 1] !== colorRegions[r][c] ? 'border-left' : '',
        r === size - 1 || colorRegions[r + 1]?.[c] !== colorRegions[r][c] ? 'border-bottom' : '',
        c === size - 1 || colorRegions[r][c + 1] !== colorRegions[r][c] ? 'border-right' : '',
        cellState === 'X' ? 'state-x' : '',
        cellState === 'queen' ? 'state-queen' : '',
        revealed?.has(k) ? 'state-revealed' : '',
        invalid ? 'state-invalid' : '',
        isAutoX ? 'state-auto-x' : '',
        isCrosshatch ? 'crosshatch' : '',
        isHintArea ? 'hint-area' : '',
        isHintCause ? 'state-hint-cause' : '',
        isHintElim ? 'state-hint-elim' : '',
        isHintPlace ? 'state-hint-place' : '',
      ].filter(Boolean).join(' ');

      cells.push(
        <div
          key={k}
          className={cls}
          data-row={r}
          data-col={c}
          style={boxShadow ? { boxShadow, zIndex: 2 } : undefined}
        >
          {cellState === 'X' ? '✕' : cellState === 'queen' ? '♛'
            : isAutoX ? '✕' : isHintElim ? '✕' : isHintPlace ? '♛' : ''}
        </div>
      );
    }
  }

  // A read-only board takes no gestures of its own, so it must not swallow the browser's —
  // `touch-action: none` on `.board` is what otherwise kills a pinch-zoom or a page scroll that
  // happens to start on top of one. `.board-static` gives it back.
  return (
    <div
      ref={boardRef}
      className={readOnly ? 'board board-static' : 'board'}
      style={{ '--board-size': String(size) } as React.CSSProperties}
    >
      {cells}
    </div>
  );
}
